// ---------------------------------------------------------------------------
// Domain types for Dental Interview Coach Local
// These mirror the SQLite schema (see src/db/schema.ts) but are the shapes the
// React/TypeScript layer works with day-to-day.
// ---------------------------------------------------------------------------

export type Id = string;

// ---------------------------------------------------------------------------
// Student profile
// ---------------------------------------------------------------------------

/** Raw, free-text inputs the student provides. */
export interface ProfileInputs {
  resume: string;
  personalStatement: string;
  dentalExperience: string;
  shadowingHours: string;
  volunteering: string;
  research: string;
  leadership: string;
  manualDexterity: string;
  whyDentistry: string;
  strengths: string;
  weaknesses: string;
  gapYear: string;
  schoolList: string;
  backgroundNotes: string;
  redFlags: string;
}

export const EMPTY_PROFILE_INPUTS: ProfileInputs = {
  resume: '', personalStatement: '', dentalExperience: '', shadowingHours: '', volunteering: '',
  research: '', leadership: '', manualDexterity: '', whyDentistry: '', strengths: '', weaknesses: '',
  gapYear: '', schoolList: '', backgroundNotes: '', redFlags: '',
};

/**
 * Metadata of an uploaded profile document (the primary application). The
 * original bytes stay in SQLite for download; only this metadata is shown in
 * the UI — never the document's content.
 */
export interface PrimaryApplicationMeta {
  profileId: Id;
  filename: string;
  mime: string;
  sizeBytes: number;
  createdAt: string;
}

/** Structured profile produced by the Profile Agent. */
export interface StructuredProfile {
  motivationForDentistry: string;
  clinicalExposure: string;
  leadershipExamples: string;
  serviceExamples: string;
  researchExamples: string;
  manualDexterityExamples: string;
  ethicalViewpoints: string;
  weakAreas: string;
  programFitArguments: Record<string, string>;
  /** Concrete, reusable "answer material" the coach can weave into answers. */
  answerMaterial: string[];
  strengths: string[];
  redFlags: string[];
}

export interface StudentProfile {
  id: Id;
  name: string;
  inputs: ProfileInputs;
  structured: StructuredProfile | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Dental programs + school fit
// ---------------------------------------------------------------------------

/**
 * Per-field provenance for school-fit claims. Keyed by the SchoolFitProfile
 * field name (mission, curriculumStyle, …). Lets the UI show which claims are
 * solid enough to repeat out loud in a real interview.
 */
export interface FitFieldSource {
  sourceUrl: string;
  confidence: ConfidenceLevel;
  /** ISO timestamp of when this claim was last researched or user-confirmed. */
  lastCheckedAt: string;
  /** True once the user manually confirmed or edited this claim. */
  confirmed?: boolean;
}

export interface SchoolFitProfile {
  mission: string;
  curriculumStyle: string;
  clinicalEmphasis: string;
  communityServiceFocus: string;
  researchFocus: string;
  location: string;
  uniquePrograms: string;
  interviewStyle: string;
  commonQuestionThemes: string[];
  /** Source metadata per fit field (optional — absent on legacy profiles). */
  sources?: Record<string, FitFieldSource>;
}

export const EMPTY_SCHOOL_FIT: SchoolFitProfile = {
  mission: '', curriculumStyle: '', clinicalEmphasis: '', communityServiceFocus: '',
  researchFocus: '', location: '', uniquePrograms: '', interviewStyle: '', commonQuestionThemes: [],
};

export interface DentalProgram {
  id: Id;
  name: string;
  shortName: string;
  fit: SchoolFitProfile;
  /** Interview date (YYYY-MM-DD) for the countdown/prep timeline, or null. */
  interviewDate: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// School dossier (deep per-school research workspace)
// ---------------------------------------------------------------------------

/** One researched dossier claim with its provenance. */
export interface DossierSection {
  content: string;
  sourceUrl: string;
  confidence: ConfidenceLevel;
}

const EMPTY_DOSSIER_SECTION: DossierSection = { content: '', sourceUrl: '', confidence: 'low' };

/**
 * A rich research dossier for one school — deeper than the fit profile.
 * Every section carries its own source + confidence so nothing is silently
 * presented as verified.
 */
export interface SchoolDossier {
  mission: DossierSection;
  curriculum: DossierSection;
  clinicalTimeline: DossierSection;
  communityClinics: DossierSection;
  researchCenters: DossierSection;
  studentOrganizations: DossierSection;
  interviewFormat: DossierSection;
  recentNews: DossierSection;
  /** Thoughtful questions the applicant can ask their interviewers. */
  questionsToAsk: string[];
  generatedAt: string;
}

export const EMPTY_DOSSIER: SchoolDossier = {
  mission: EMPTY_DOSSIER_SECTION,
  curriculum: EMPTY_DOSSIER_SECTION,
  clinicalTimeline: EMPTY_DOSSIER_SECTION,
  communityClinics: EMPTY_DOSSIER_SECTION,
  researchCenters: EMPTY_DOSSIER_SECTION,
  studentOrganizations: EMPTY_DOSSIER_SECTION,
  interviewFormat: EMPTY_DOSSIER_SECTION,
  recentNews: EMPTY_DOSSIER_SECTION,
  questionsToAsk: [],
  generatedAt: '',
};

// ---------------------------------------------------------------------------
// Question bank
// ---------------------------------------------------------------------------

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export type SourceType = 'official' | 'unofficial' | 'manual' | 'generated';

export type QuestionQuality =
  | 'high_value'
  | 'common'
  | 'school_specific'
  | 'outdated'
  | 'generic'
  | 'duplicate'
  | 'low_confidence';

export const QUESTION_CATEGORIES = [
  'Why dentistry?',
  'Why this school?',
  'Tell me about yourself',
  'Ethical scenarios',
  'Manual dexterity',
  'Teamwork',
  'Leadership',
  'Failure / weakness',
  'Conflict',
  'Diversity / cultural competence',
  'Healthcare policy',
  'Community service',
  'Academic difficulty',
  'Research',
  'Dental public health',
  'MMI stations',
  'Traditional interview questions',
  'Behavioral questions',
] as const;

export type QuestionCategory = (typeof QUESTION_CATEGORIES)[number];

/**
 * Curation state for researched questions. Automated research lands as
 * 'needs_review'; manual imports default to 'accepted'.
 */
export type ReviewStatus =
  | 'needs_review'
  | 'accepted'
  | 'rejected'
  | 'needs_verification'
  | 'duplicate'
  | 'outdated'
  | 'high_value';

export const REVIEW_STATUSES: ReviewStatus[] = [
  'needs_review',
  'accepted',
  'rejected',
  'needs_verification',
  'duplicate',
  'outdated',
  'high_value',
];

export interface InterviewQuestion {
  id: Id;
  question: string;
  programId: Id | null; // null = general / applies to any school
  category: QuestionCategory;
  source: string;
  sourceUrl: string;
  sourceType: SourceType;
  year: string;
  tags: string[];
  confidence: ConfidenceLevel;
  quality: QuestionQuality;
  // Per-student state
  favorite: boolean;
  practiced: boolean;
  weak: boolean;
  hidden: boolean;
  notes: string;
  // Research review workflow (v5)
  reviewStatus: ReviewStatus;
  /** When the user last reviewed/checked this question, or null. */
  reviewedAt: string | null;
  reviewNotes: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

export interface GeneratedAnswer {
  strongAnswer: string;
  shortVersion: string; // ~60s
  longVersion: string; // ~2min
  bulletStructure: string[];
  whatNotToSay: string[];
  followUps: string[];
  personalDetailsUsed: string[];
}

export interface SavedAnswer {
  id: Id;
  questionId: Id;
  generated: GeneratedAnswer | null;
  finalAnswer: string; // user's edited version
  isBest: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/** A previous final answer snapshot, written whenever a save changes the text. */
export interface SavedAnswerVersion {
  id: Id;
  answerId: Id;
  finalAnswer: string;
  savedAt: string;
}

// ---------------------------------------------------------------------------
// Rubric + scoring
// ---------------------------------------------------------------------------

export interface RubricCriterion {
  key: string;
  label: string;
  weight: number; // 0..1, all weights sum to 1
  meaning: string;
}

export interface Rubric {
  version: string;
  criteria: RubricCriterion[];
}

/** What the Grading Agent returns. Final score is NOT decided by the AI. */
export interface GradingResult {
  rubricVersion: string;
  scores: Record<string, number>; // criterion key -> 1..5
  evidence: Record<string, string>;
  flags: string[];
  feedback: string;
  strengths: string[];
  weaknesses: string[];
  missingDetails: string[];
  suggestedImprovedAnswer: string;
  sentenceImprovements: string[];
}

/** Computed transcript analytics (deterministic, code-driven). */
export interface VoiceAnalytics {
  wordCount: number;
  durationSeconds: number;
  wordsPerMinute: number;
  fillerWordCount: number;
  fillerWords: Record<string, number>;
  repeatedWords: Record<string, number>;
  longPauses: number;
  confidenceMarkers: number;
  hedgingMarkers: number;
}

export interface ScoredAttempt {
  id: Id;
  questionId: Id;
  sessionId: Id | null;
  mockInterviewId: Id | null;
  transcriptText: string;
  normalizedHash: string;
  rubricVersion: string;
  promptVersion: string;
  model: string;
  scores: Record<string, number>;
  finalScore: number; // 0..100
  grading: GradingResult;
  analytics: VoiceAnalytics;
  cached: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Practice + mock interviews
// ---------------------------------------------------------------------------

export type SessionMode = 'infinite' | 'mock';

export interface PracticeSession {
  id: Id;
  mode: SessionMode;
  createdAt: string;
}

export type InterviewType = 'Traditional' | 'MMI' | 'Open-file' | 'Closed-file' | 'Mixed';
export type Difficulty = 'Easy' | 'Realistic' | 'Hard' | 'Stress interview';

export interface MockInterviewConfig {
  programId: Id | null;
  durationMinutes: number;
  interviewType: InterviewType;
  difficulty: Difficulty;
}

export interface MockInterviewQuestionPlan {
  questionId: Id | null;
  question: string;
  category: QuestionCategory;
  role: string; // e.g. "warm-up", "behavioral", "ethical/MMI", "closing"
  suggestedMinutes: number;
}

export interface MockInterview {
  id: Id;
  config: MockInterviewConfig;
  plan: MockInterviewQuestionPlan[];
  attemptIds: Id[];
  report: MockInterviewReport | null;
  status: 'planned' | 'in_progress' | 'completed';
  /** Index of the question the candidate is on — lets an in-progress mock resume. */
  currentStep: number;
  createdAt: string;
  completedAt: string | null;
}

export interface MockInterviewReport {
  overallReadiness: number; // 0..100
  categoryScores: Record<string, number>;
  bestAnswer: { questionId: Id; question: string; score: number } | null;
  worstAnswer: { questionId: Id; question: string; score: number } | null;
  repeatedProblems: string[];
  missedOpportunities: string[];
  recommendations: string[];
  questionsToRetry: string[];
  summary: string;
}

// ---------------------------------------------------------------------------
// Video practice (webcam) self-review
// ---------------------------------------------------------------------------

/** Manual self-assessment rating for one delivery dimension. */
export type SelfRating = '' | 'good' | 'mixed' | 'needs_work';

/**
 * The user's OWN review of their recorded video. Deliberately manual — there
 * is no automated body-language analysis; if one is added later it must be
 * clearly labeled as such.
 */
export interface AttemptVideoReview {
  eyeContact: SelfRating;
  posture: SelfRating;
  /** Distracting habits the user spotted (e.g. "fidgeting", "touching face"). */
  habits: string[];
  notes: string;
}

export const EMPTY_VIDEO_REVIEW: AttemptVideoReview = {
  eyeContact: '',
  posture: '',
  habits: [],
  notes: '',
};

// ---------------------------------------------------------------------------
// Red Flag Coach (difficult personal topics)
// ---------------------------------------------------------------------------

/** AI-personalized coaching for one difficult topic in the student's profile. */
export interface RedFlagTopicPlan {
  /** Topic name, e.g. "Low GPA" or "Gap year". */
  topic: string;
  /** Why interviewers probe this and what they are really asking. */
  whyItMatters: string;
  /** Safe answer structure, one beat per entry, grounded in the profile. */
  answerStructure: string[];
  likelyFollowUps: string[];
  doNotSay: string[];
  /** Concrete profile facts to weave in as evidence of growth. */
  suggestedPoints: string[];
}

export interface RedFlagPlan {
  topics: RedFlagTopicPlan[];
  generalAdvice: string;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Settings + AI config
// ---------------------------------------------------------------------------

export type AIProviderKind = 'codex';

/** Codex reasoning effort. Lower = much faster/cheaper; higher = slower/deeper. */
export type ReasoningEffort = 'low' | 'medium' | 'high';

export type ThemeMode = 'light' | 'dark';

/** Color theme, independent of light/dark mode.
 *  'navy'  — deep navy primary with a muted dental-teal accent (default).
 *  'slate' — softer slate-blue primary with a mint / soft-cyan accent. */
export type ThemePalette = 'navy' | 'slate';

export interface AppSettings {
  aiProvider: AIProviderKind;
  /** Path to the Codex CLI executable. Empty = use `codex` on PATH. */
  codexPath: string;
  /** Model passed as `-m`. Empty = the CLI's configured default model. */
  defaultModel: string;
  /** Passed to Codex as `-c model_reasoning_effort`. Default 'medium' keeps
   *  web-search research under the timeout; the model default (xhigh) is slow. */
  reasoningEffort: ReasoningEffort;
  /** Single switch for web-search research (the old cached/live "mode" had no effect). */
  enableWebSearch: boolean;
  workingDirectory: string;
  timeoutSeconds: number;
  /** Automatic re-prompts when the model returns malformed/invalid JSON. */
  maxRetries: number;
  ttsVoice: string;
  /** Microphone deviceId used for audio recording (playback + delivery review). */
  microphoneId: string;
  /** Default the webcam toggle ON in recorders (video practice mode). */
  enableVideoPractice: boolean;
  rubricVersion: string;
  promptVersion: string;
  themeMode: ThemeMode;
  themePalette: ThemePalette;
  /** When the first-run wizard finished (ISO). Null = show onboarding. */
  onboardedAt: string | null;
}

export const DEFAULT_SETTINGS: AppSettings = {
  aiProvider: 'codex',
  // Empty path falls back to `codex` on PATH (both the Rust command and the
  // Vite bridge do this) — never ship a developer machine's absolute path.
  codexPath: '',
  // Empty model uses the CLI's own configured default.
  defaultModel: '',
  reasoningEffort: 'medium',
  enableWebSearch: true,
  workingDirectory: '',
  timeoutSeconds: 300,
  maxRetries: 1,
  ttsVoice: '',
  microphoneId: '',
  enableVideoPractice: false,
  rubricVersion: 'dental-interview-v1.0',
  promptVersion: 'prompts-v1.5',
  themeMode: 'light',
  themePalette: 'navy',
  onboardedAt: null,
};

// ---------------------------------------------------------------------------
// Prompt templates
// ---------------------------------------------------------------------------

export type PromptAgent =
  | 'profile'
  | 'intake'
  | 'research'
  | 'fit'
  | 'dossier'
  | 'answer'
  | 'grading'
  | 'mock'
  | 'improvement'
  | 'followup'
  | 'redflag';

export interface PromptTemplate {
  id: Id;
  agent: PromptAgent;
  version: string;
  label: string;
  template: string;
  updatedAt: string;
}

/** A superseded prompt template body, kept so edits can be reviewed/restored. */
export interface PromptTemplateHistoryEntry {
  id: Id;
  agent: PromptAgent;
  version: string;
  template: string;
  savedAt: string;
}

// ---------------------------------------------------------------------------
// Improvement plans (persisted analytics output)
// ---------------------------------------------------------------------------

/** The Improvement Plan Agent's output (mirrors ai/provider.ts ImprovementPlan). */
export interface ImprovementPlanContent {
  topWeaknesses: string[];
  patterns: string[];
  recommendedQuestionCategories: string[];
  drills: string[];
  summary: string;
}

export interface ImprovementPlanRecord {
  id: Id;
  plan: ImprovementPlanContent;
  createdAt: string;
  /** How many graded attempts existed when the plan was built. */
  sourceAttemptCount: number;
  /** Average score at creation — later attempts are compared against this. */
  baselineAverage: number;
  /** Indexes into plan.drills the user has checked off. */
  completedDrills: number[];
}

// ---------------------------------------------------------------------------
// Spaced repetition (SM-2)
// ---------------------------------------------------------------------------

export interface ReviewState {
  questionId: Id;
  /** SM-2 ease factor (>= 1.3). */
  ease: number;
  /** Days until the next review at the time it was scheduled. */
  intervalDays: number;
  /** Consecutive successful reviews. */
  repetitions: number;
  /** ISO timestamp when the question is due again. */
  dueAt: string;
  lastScore: number;
  updatedAt: string;
}
